import React from 'react';

// ── Shared Invoice letterhead ───────────────────────────────────────────────
// This is the SAME visual recipe as BillingSettings.js's live preview
// (Corporate / Classic / Bold, driven by the firm's saved settings), but
// parameterized to take REAL invoice data instead of hardcoded example rows.
// Finance.js renders this off-screen and rasterizes it with html2pdf so the
// downloaded PDF can never drift from what the settings preview shows —
// they are, byte for byte, the same component.

export const STYLE_CFG = {
  classic:   { headerBg:'#0f1f45', headerText:'#fff', theadBg:'#0f1f45', theadText:'#fff', totalBg:'#0f1f45', totalText:'#fff' },
  corporate: { headerBg:'#ffffff', headerText:'#0f1f45', theadBg:'#0f1f45', theadText:'#fff', totalBg:'#0f1f45', totalText:'#fff' },
  bold:      { headerBg:'#1c1c2e', headerText:'#fff', theadBg:'#f97316', theadText:'#fff', totalBg:'#f97316', totalText:'#fff' },
};

export const LOGO_SLOT_W = 200, LOGO_SLOT_H = 64;

export function shade(hex, amt) {
  const c = (hex || '#0f1f45').replace('#', '');
  const num = parseInt(c.length === 3 ? c.split('').map(x => x + x).join('') : c, 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amt));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amt));
  const b = Math.min(255, Math.max(0, (num & 0xff) + amt));
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

export function relativeLuminance(hex) {
  const c = (hex || '#0f1f45').replace('#', '');
  const num = parseInt(c.length === 3 ? c.split('').map(x => x + x).join('') : c, 16);
  const [r, g, b] = [(num >> 16) / 255, ((num >> 8) & 0xff) / 255, (num & 0xff) / 255];
  const lin = v => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

// Resolves the item-table header / party badges / total-band colors.
// header_style: 'navy' (fixed, matches the template's own default — unchanged
// behavior) | 'primary' (flat Primary Color, text auto-contrasted) | 'gradient'
// (self-shaded Primary Color, text auto-contrasted).
export function resolveHeaderColors(templateCfg, primaryColor, headerStyle) {
  if (!headerStyle || headerStyle === 'navy') {
    return { bg: templateCfg.theadBg, text: templateCfg.theadText };
  }
  const bg = headerStyle === 'gradient'
    ? `linear-gradient(90deg, ${shade(primaryColor, -40)} 0%, ${primaryColor} 100%)`
    : primaryColor;
  const text = relativeLuminance(primaryColor) > 0.5 ? '#16171c' : '#ffffff';
  return { bg, text };
}

export const fc = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
export const fd = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

export function PositionedLogo({ logoUrl, x, y, scale }) {
  return (
    <div style={{ width: LOGO_SLOT_W, height: LOGO_SLOT_H, overflow: 'hidden', position: 'relative' }}>
      <img src={logoUrl} alt="Logo" style={{
        position: 'absolute', left: '50%', top: '50%',
        maxWidth: LOGO_SLOT_W - 20, maxHeight: LOGO_SLOT_H - 12, width: 'auto', height: 'auto', objectFit: 'contain',
        transform: `translate(-50%,-50%) translate(${x || 0}px,${y || 0}px) scale(${scale || 1})`,
      }} />
    </div>
  );
}

export default function InvoiceLetterhead({ settings = {}, invoice = {}, items = null, qrDataUrl = '' }) {
  const s = settings;
  const templateStyle = s.template_style || 'corporate';
  const cfg = STYLE_CFG[templateStyle] || STYLE_CFG.corporate;
  const previewColor = s.primary_color || '#0f1f45';
  const isCorporate = templateStyle === 'corporate';
  const isBold = templateStyle === 'bold';
  const isGstRegistered = s.is_gst_registered !== false;
  const font = s.invoice_font || 'Plus Jakarta Sans';

  const theadColors = resolveHeaderColors(cfg, previewColor, s.header_style);

  const rows = items || invoice.items || [];
  const isGst = invoice.gst_type && invoice.gst_type !== 'none' && Number(invoice.total_gst) > 0;
  const docTitle = s.invoice_heading || (isGst ? 'Tax Invoice' : 'Invoice');
  const showDiscountCol = s.show_discount_column !== false;
  const showScope = s.show_scope_of_work !== false;

  // Subtotal shown is the GROSS line-item total (before any discount) — never
  // the already-net figure some older invoice rows stored under `subtotal`.
  // Discount is whichever is larger: what was actually given away item-by-item,
  // or the invoice's own aggregate discount (an aggregate discount can only
  // ever top up the item-level total, never undercut it — see CreateInvoice's
  // handleSubmit for where that floor is enforced).
  const grossSubtotal = rows.reduce((sum, r) => sum + Number(r.amount != null ? r.amount : Number(r.quantity || 1) * Number(r.rate || 0)), 0);
  const itemDiscountTotal = rows.reduce((sum, r) => sum + Number(r.discount_amount || 0), 0);
  const combinedDiscount = Math.max(itemDiscountTotal, Number(invoice.discount_amount || 0));

  const firmAddr = invoice.firm_full_address || s.firm_address || '';
  const clientAddrParts = [invoice.client_address, invoice.client_city, invoice.client_state, invoice.client_pincode].filter(Boolean);

  return (
    <div style={{ background: '#fff', fontFamily: font, color: '#1a1a1a', fontSize: '11px', width: '794px', position: 'relative' }}>

      {/* HEADER */}
      {isCorporate ? (
        <div style={{ background: '#fff', borderBottom: `3px solid ${previewColor}`, padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 0 }}>
          <div style={{ width: 5, background: previewColor, borderRadius: '3px 0 0 3px', alignSelf: 'stretch', marginRight: 16, flexShrink: 0 }}></div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
              {s.logo_url
                ? <PositionedLogo logoUrl={s.logo_url} x={s.logo_x} y={s.logo_y} scale={s.logo_scale} />
                : <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '40px', padding: '6px 14px', background: `${previewColor}18`, border: `2px solid ${previewColor}30`, borderRadius: 6, alignSelf: 'flex-start' }}>
                    <span style={{ color: previewColor, fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.5px' }}>{s.firm_name || 'LCCA'}</span>
                  </div>}
              <div>
                <div style={{ fontSize: '15px', fontWeight: '800', color: previewColor, letterSpacing: '0.2px', lineHeight: 1.2 }}>{s.firm_name || 'CHANAKYA X'}</div>
                <div style={{ fontSize: '9px', color: '#666', marginTop: 4, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{firmAddr}{'\n'}{s.firm_phone_num || s.firm_mobile ? `Mobile: ${s.firm_phone_num || s.firm_mobile}` : ''}{s.firm_email ? ` | ${s.firm_email}` : ''}{s.firm_website ? ` | ${s.firm_website}` : ''}</div>
                {s.firm_gstin && <div style={{ marginTop: 3, fontSize: '9px', color: '#888' }}>GSTIN: <strong style={{ color: previewColor }}>{s.firm_gstin}</strong></div>}
              </div>
            </div>
          </div>
          <div style={{ textAlign: 'right', paddingLeft: 18, borderLeft: '1px solid #f0f0f0', flexShrink: 0 }}>
            <div style={{ fontSize: '20px', fontWeight: '800', color: previewColor, letterSpacing: '1px' }}>{docTitle}</div>
            <div style={{ fontSize: '8px', background: previewColor, color: '#fff', padding: '2px 10px', display: 'inline-flex', alignItems: 'center', lineHeight: 1.4, marginTop: 5, borderRadius: 3, letterSpacing: '0.5px' }}>ORIGINAL FOR RECIPIENT</div>
            <div style={{ marginTop: 8, fontSize: '10px', color: '#555', lineHeight: 2 }}>
              <div><span style={{ color: '#999' }}>Invoice No. </span><strong style={{ color: '#111' }}>{invoice.invoice_number || '—'}</strong></div>
              <div><span style={{ color: '#999' }}>Date </span><strong style={{ color: '#111' }}>{fd(invoice.invoice_date)}</strong></div>
              {invoice.due_date && <div><span style={{ color: '#999' }}>Due Date </span><strong style={{ color: '#dc2626' }}>{fd(invoice.due_date)}</strong></div>}
            </div>
          </div>
        </div>
      ) : isBold ? (
        <div style={{ background: cfg.headerBg }}>
          <div style={{ padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '55%' }}>
              {s.logo_url
                ? <PositionedLogo logoUrl={s.logo_url} x={s.logo_x} y={s.logo_y} scale={s.logo_scale} />
                : <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '40px', padding: '6px 14px', background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(255,255,255,0.25)', borderRadius: 6, alignSelf: 'flex-start' }}>
                    <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.5px' }}>{s.firm_name || 'LCCA'}</span>
                  </div>}
              <div>
                <div style={{ fontSize: '15px', fontWeight: '800', color: '#fff', letterSpacing: '0.4px' }}>{s.firm_name || 'CHANAKYA X'}</div>
                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.7)', marginTop: 4, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{firmAddr}{'\n'}{s.firm_phone_num || s.firm_mobile || ''}{s.firm_email ? ` | ${s.firm_email}` : ''}</div>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '22px', fontWeight: '800', color: '#f97316', letterSpacing: '1px' }}>{docTitle}</div>
              <div style={{ fontSize: '8px', border: '1px solid rgba(249,115,22,0.6)', color: '#f97316', padding: '2px 10px', display: 'inline-flex', alignItems: 'center', lineHeight: 1.4, marginTop: 5, borderRadius: 3, letterSpacing: '0.5px' }}>ORIGINAL FOR RECIPIENT</div>
              <div style={{ marginTop: 8, fontSize: '10px', color: 'rgba(255,255,255,0.85)', lineHeight: 2 }}>
                <div><span style={{ opacity: 0.7 }}>Invoice No. </span><strong>{invoice.invoice_number || '—'}</strong></div>
                <div><span style={{ opacity: 0.7 }}>Date </span><strong>{fd(invoice.invoice_date)}</strong></div>
                {invoice.due_date && <div><span style={{ opacity: 0.7 }}>Due Date </span><strong style={{ color: '#fbbf24' }}>{fd(invoice.due_date)}</strong></div>}
              </div>
            </div>
          </div>
          <div style={{ height: 4, background: '#f97316' }}></div>
        </div>
      ) : (
        <div style={{ background: cfg.headerBg, padding: '20px 24px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '55%' }}>
            {s.logo_url
              ? <PositionedLogo logoUrl={s.logo_url} x={s.logo_x} y={s.logo_y} scale={s.logo_scale} />
              : <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '40px', padding: '6px 14px', background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 4, alignSelf: 'flex-start' }}>
                  <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.5px' }}>{s.firm_name || 'LCCA'}</span>
                </div>}
            <div>
              <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#fff', letterSpacing: '0.3px', lineHeight: 1.2 }}>{s.firm_name || 'CHANAKYA X'}</div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.82)', marginTop: 4, lineHeight: 1.8, whiteSpace: 'pre-line' }}>{firmAddr}{'\n'}{s.firm_phone_num || s.firm_mobile ? `Mobile: ${s.firm_phone_num || s.firm_mobile}` : ''}{s.firm_email ? ` | ${s.firm_email}` : ''}{s.firm_website ? ` | ${s.firm_website}` : ''}</div>
              {s.firm_gstin && <div style={{ marginTop: 3, fontSize: '9px', color: 'rgba(255,255,255,0.6)' }}>GSTIN: <strong style={{ color: 'rgba(255,255,255,0.9)' }}>{s.firm_gstin}</strong></div>}
            </div>
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#fff', letterSpacing: '1.5px' }}>{docTitle}</div>
            <div style={{ fontSize: '8px', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', padding: '2px 8px', display: 'inline-flex', alignItems: 'center', lineHeight: 1.4, marginTop: 4, borderRadius: 2 }}>ORIGINAL FOR RECIPIENT</div>
            <div style={{ marginTop: 8, fontSize: '10px', color: 'rgba(255,255,255,0.9)', lineHeight: 2 }}>
              <div><span style={{ opacity: 0.7 }}>Invoice No. </span><strong>{invoice.invoice_number || '—'}</strong></div>
              <div><span style={{ opacity: 0.7 }}>Date </span><strong>{fd(invoice.invoice_date)}</strong></div>
              {invoice.due_date && <div><span style={{ opacity: 0.7 }}>Due Date </span><strong>{fd(invoice.due_date)}</strong></div>}
            </div>
          </div>
        </div>
      )}

      {/* SEPARATOR */}
      {templateStyle === 'classic' && <div style={{ display: 'flex', height: '5px' }}><div style={{ flex: 1, background: previewColor }}></div><div style={{ flex: 1, background: '#c8a951' }}></div><div style={{ flex: 1, background: '#e8d9a0' }}></div></div>}
      {templateStyle === 'corporate' && <div style={{ height: '2px', background: '#0f1f45' }}></div>}

      {/* BILL TO */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid #eee' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '9px', fontWeight: 'bold', background: theadColors.bg, color: theadColors.text, padding: '2px 8px', display: 'inline-flex', alignItems: 'center', lineHeight: 1.4, borderRadius: '2px', marginBottom: '7px', letterSpacing: '0.4px' }}>BILL TO</div>
            <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1a1a1a' }}>{invoice.client_name || 'Client Name'}</div>
            <div style={{ fontSize: '10px', color: '#555', lineHeight: 1.7, marginTop: '2px' }}>
              {clientAddrParts.join(', ') || `Client Address, ${s.firm_city || 'City'}`}
              {(invoice.client_gstin || invoice.client_mobile) && <><br />{invoice.client_gstin ? `GSTIN: ${invoice.client_gstin}` : ''}{invoice.client_gstin && invoice.client_mobile ? ' | ' : ''}{invoice.client_mobile ? `Mobile: ${invoice.client_mobile}` : ''}</>}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '7px' }}>Your Firm Details</div>
            <div style={{ fontSize: '10px', color: '#555', lineHeight: 1.9 }}>
              {s.firm_gstin && <div><span style={{ color: '#888' }}>GSTIN: </span><strong style={{ color: '#1a1a1a' }}>{s.firm_gstin}</strong></div>}
              {s.firm_pan && <div><span style={{ color: '#888' }}>PAN: </span><strong style={{ color: '#1a1a1a' }}>{s.firm_pan}</strong></div>}
              {s.firm_state && <div><span style={{ color: '#888' }}>State: </span><strong style={{ color: '#1a1a1a' }}>{s.firm_state}</strong></div>}
            </div>
          </div>
        </div>
      </div>

      {/* TABLE */}
      <div style={{ padding: '0 24px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead>
            <tr style={{ background: theadColors.bg, color: theadColors.text }}>
              <th style={{ padding: '7px 8px', textAlign: 'center', width: '26px' }}>#</th>
              <th style={{ padding: '7px 8px', textAlign: 'left' }}>Description of Service</th>
              {s.show_sac_code !== false && <th style={{ padding: '7px 8px', textAlign: 'center', width: '40px' }}>SAC</th>}
              <th style={{ padding: '7px 8px', textAlign: 'right', width: '65px' }}>Rate (₹)</th>
              {showDiscountCol && <th style={{ padding: '7px 8px', textAlign: 'right', width: '55px' }}>Discount</th>}
              {isGstRegistered && <th style={{ padding: '7px 8px', textAlign: 'center', width: '40px' }}>GST%</th>}
              <th style={{ padding: '7px 8px', textAlign: 'right', width: '65px' }}>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {(rows.length ? rows : [{ particular: 'Service', quantity: 1, rate: invoice.total_amount || 0, amount: invoice.total_amount || 0, net_amount: invoice.total_amount || 0 }]).map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#f9f9f9' : '#fff' }}>
                <td style={{ padding: '8px 8px', textAlign: 'center', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{i + 1}</td>
                <td style={{ padding: '8px 8px', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>
                  <div style={{ fontWeight: 'bold', color: previewColor, fontSize: '10.5px', marginBottom: '2px' }}>{row.particular || row.description || 'Service'}</div>
                  {showScope && row.particular && row.description && (
                    <div style={{ fontSize: '9px', color: '#555', lineHeight: 1.6, borderTop: '1px dashed #ddd', paddingTop: '4px', marginTop: '3px', whiteSpace: 'pre-line' }}>{row.description}</div>
                  )}
                </td>
                {s.show_sac_code !== false && <td style={{ padding: '8px 8px', textAlign: 'center', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{row.sac_code || '—'}</td>}
                <td style={{ padding: '8px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{fc(row.rate)}</td>
                {showDiscountCol && <td style={{ padding: '8px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee', color: '#e8502a' }}>{Number(row.discount_amount) > 0 ? fc(row.discount_amount) : '—'}</td>}
                {isGstRegistered && <td style={{ padding: '8px 8px', textAlign: 'center', verticalAlign: 'top', borderBottom: '1px solid #eee' }}>{invoice.gst_type === 'igst' ? '18%' : invoice.gst_type === 'cgst_sgst' ? '18%' : '—'}</td>}
                <td style={{ padding: '8px 8px', textAlign: 'right', verticalAlign: 'top', borderBottom: '1px solid #eee', fontWeight: 'bold' }}>{fc(row.net_amount != null ? row.net_amount : row.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* BOTTOM: BANK | QR | TOTALS */}
      <div style={{ display: 'grid', gridTemplateColumns: s.show_invoice_qr !== false && s.upi_id ? '1.2fr 0.8fr 1fr' : '1fr 1fr', gap: 0, padding: '12px 24px 0', borderTop: '1px solid #eee' }}>
        <div style={{ paddingRight: '12px', borderRight: '1px solid #eee', fontSize: '10px' }}>
          <div style={{ fontWeight: 'bold', color: previewColor, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '9px' }}>Bank Details</div>
          <div style={{ lineHeight: 1.9, color: '#444' }}>
            {s.bank_name && <div><span style={{ color: '#888', display: 'inline-block', width: '52px' }}>Name:</span>{s.bank_name}</div>}
            {s.bank_account && <div><span style={{ color: '#888', display: 'inline-block', width: '52px' }}>Account:</span>{s.bank_account}</div>}
            {s.bank_ifsc && <div><span style={{ color: '#888', display: 'inline-block', width: '52px' }}>IFSC:</span>{s.bank_ifsc}</div>}
            {s.bank_branch && <div><span style={{ color: '#888', display: 'inline-block', width: '52px' }}>Branch:</span>{s.bank_branch}</div>}
          </div>
        </div>
        {s.show_invoice_qr !== false && s.upi_id && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 12px', borderRight: '1px solid #eee' }}>
            {qrDataUrl && <img src={qrDataUrl} alt="UPI QR" style={{ width: '80px', height: '80px', borderRadius: '4px', display: 'block' }} />}
            <div style={{ marginTop: '6px', textAlign: 'center' }}>
              <div style={{ fontSize: '8px', fontWeight: 'bold', color: previewColor }}>Scan to Pay</div>
              <div style={{ fontSize: '8px', color: '#777', marginTop: '2px' }}>{s.upi_id}</div>
            </div>
          </div>
        )}
        <div style={{ paddingLeft: '14px', fontSize: '11px' }}>
          <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee' }}><span style={{ color: '#666' }}>Subtotal</span><span>{fc(grossSubtotal)}</span></div>
            {combinedDiscount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee', color: '#e8502a' }}><span>Discount</span><span>— {fc(combinedDiscount)}</span></div>}
            {isGst && invoice.gst_type === 'cgst_sgst' && <>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee' }}><span style={{ color: '#666' }}>CGST</span><span>{fc(invoice.cgst_amount)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee' }}><span style={{ color: '#666' }}>SGST</span><span>{fc(invoice.sgst_amount)}</span></div>
            </>}
            {isGst && invoice.gst_type === 'igst' && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee' }}><span style={{ color: '#666' }}>IGST</span><span>{fc(invoice.igst_amount)}</span></div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: theadColors.bg, color: theadColors.text, fontWeight: 'bold', fontSize: '12px' }}><span>Total</span><span>{fc(invoice.total_amount)}</span></div>
          </div>
          {s.show_ledger_balance && (
            <div style={{ border: '0.5px solid #e8e8e8', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', borderBottom: '0.5px solid #eee', fontSize: '10px' }}><span style={{ color: '#666' }}>Received</span><span style={{ color: '#16a34a', fontWeight: 'bold' }}>{fc(invoice.amount_paid)}</span></div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 10px', fontWeight: 'bold' }}><span style={{ color: '#dc2626' }}>Balance Due</span><span style={{ color: '#dc2626' }}>{fc(invoice.balance_due)}</span></div>
            </div>
          )}
        </div>
      </div>

      {/* AMOUNT IN WORDS */}
      {invoice.amount_in_words && (
        <div style={{ margin: '10px 24px 0', padding: '7px 10px', borderRadius: '4px', fontSize: '10px', background: '#eef1f8', borderLeft: `3px solid ${previewColor}` }}>
          <span style={{ color: '#888', fontWeight: 'bold' }}>Amount in Words: </span>
          <em style={{ color: '#333' }}>{invoice.amount_in_words}</em>
        </div>
      )}

      {/* FOOTER */}
      <div style={{ margin: '10px 24px 0', padding: '10px 0 18px', borderTop: '1px solid #eee', display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '14px' }}>
        <div style={{ fontSize: '9px', color: '#555', lineHeight: 1.8 }}>
          {s.invoice_footer_html
            ? <div dangerouslySetInnerHTML={{ __html: s.invoice_footer_html }} />
            : s.terms_and_conditions
              ? <><div style={{ fontWeight: 'bold', color: '#333', marginBottom: '3px' }}>Terms &amp; Conditions</div>
                  {s.terms_and_conditions.split('\n').filter(Boolean).slice(0, 5).map((t, i) => <div key={i}>{t}</div>)}</>
              : null}
        </div>
        <div style={{ textAlign: 'center', fontSize: '10px' }}>
          {s.signature_url && <img src={s.signature_url} alt="Sig" style={{ maxHeight: '36px', maxWidth: '90px', display: 'block', margin: '0 auto 4px' }} />}
          <div style={{ width: '90px', height: '32px', borderBottom: '1px solid #999', margin: '0 auto 4px' }}></div>
          <div style={{ fontWeight: 'bold', color: '#333', fontSize: '10px' }}>{s.signature_name || 'Authorised Signature'}</div>
          <div style={{ color: '#555', fontSize: '8.5px', marginTop: '1px' }}>{s.signature_designation ? s.signature_designation + ' | ' : ''}For {s.firm_name || 'Your Firm Name'}</div>
        </div>
      </div>

      {/* WATERMARK */}
      {s.show_watermark && s.watermark_text && (
        <div style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%) rotate(-35deg)', fontSize: '72px', fontWeight: 'bold', color: 'rgba(0,0,0,0.05)', pointerEvents: 'none', textTransform: 'uppercase', letterSpacing: '8px', whiteSpace: 'nowrap' }}>
          {s.watermark_text}
        </div>
      )}
    </div>
  );
}
